import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { io, Socket } from "socket.io-client";
import { BrowserStorageService } from '../browser-storage/browser-storage.service';
import { BehaviorSubject, Subject } from 'rxjs';
import { NegotiationService } from '../negotiation/negotiation.service';
import { CommonService } from '../Common-function/common.service';
import { AuthService } from '../Auth/auth.service';
import { UserDetails } from 'src/app/models/user-details.model';
import { environment } from 'src/environments/environment';

export interface EventStatusChangedPayload {
  eventId: string;
  status: string;
  title?: string;
  meetCode?: string;
  description?: string;
}

@Injectable({
  providedIn: 'root'
})
export class SocketIoService implements OnDestroy {
  socket: Socket | null = null;
  isMessageDetected: BehaviorSubject<any> = new BehaviorSubject("");
  newNegotiationDetected: BehaviorSubject<any> = new BehaviorSubject("");
  notificationArrayList: BehaviorSubject<[]> = new BehaviorSubject([]);
  negotiationList: Array<any> = [];
  currentUserData: UserDetails | null = null;
  private currentAuthToken: string = "";
  private readonly socketUrl: string = environment.apiBaseUrl.replace(/\/api\/v\d+\/?$/, '');
  private legacyFallbackTimer: any = null;
  private isSocketInitialized: boolean = false;
  private readonly eventStatusChangedSubject = new Subject<EventStatusChangedPayload>();
  public readonly eventStatusChanged$ = this.eventStatusChangedSubject.asObservable();
  private readonly onWindowFocus = () => {
    this.syncNotificationsOnFocus();
  };

  constructor(private browserStorage: BrowserStorageService,
    private negotiationService: NegotiationService,
    private authservice: AuthService,
    private commonFunction: CommonService,
    private ngZone: NgZone) {
    this.authservice.currentUserDetails.subscribe((res) => {
      if (res &&  Object.keys(res).length > 0) {
          this.currentUserData = res;
      } else {
          this.currentUserData = null;
      }
    });
    this.browserStorage.sessionDataChanges.subscribe((sessionData) => {
      const nextToken = sessionData?.access_token || "";
      if (this.isSocketInitialized) {
        this.connectOrReconnect(nextToken);
      } else {
        this.currentAuthToken = nextToken;
      }
    });
    window.addEventListener('focus', this.onWindowFocus);
  }

  public initializeConnection() {
    if (this.isSocketInitialized) {
      return;
    }
    this.isSocketInitialized = true;
    this.connectOrReconnect(this.currentAuthToken);
  }

  private connectOrReconnect(token: string = "") {
    if (!this.socket) {
      this.createSocketConnection(token);
      return;
    }

    if (this.currentAuthToken !== token) {
      this.teardownSocket();
      this.createSocketConnection(token);
      return;
    }

    if (!this.socket.connected) {
      this.applySocketAuth(token);
      this.socket.connect();
    }
  }

  private createSocketConnection(token: string = "") {
    this.currentAuthToken = token;
    const socketOptions: any = {};
    if (token) {
      socketOptions.auth = { token };
      socketOptions.transportOptions = {
        polling: {
          extraHeaders: {
            token
          }
        }
      };
    }
    this.socket = io(this.socketUrl, socketOptions);
    this.registerSocketListeners();
  }

  private applySocketAuth(token: string) {
    if (!this.socket) {
      return;
    }
    const normalizedToken = token || "";
    this.socket.auth = normalizedToken ? { token: normalizedToken } : {};
    const ioOptions: any = this.socket.io?.opts;
    if (ioOptions?.transportOptions?.polling?.extraHeaders) {
      if (normalizedToken) {
        ioOptions.transportOptions.polling.extraHeaders.token = normalizedToken;
      } else {
        delete ioOptions.transportOptions.polling.extraHeaders.token;
      }
    }
  }

  private registerSocketListeners() {
    if (!this.socket) {
      return;
    }

    this.socket.on("MESSAGE", (data: any) => {
      this.ngZone.run(() => {
        this.handleLegacyMessage(data);
      });
    });

    this.socket.on("NOTIFICATION_CREATED", (notification: any) => {
      this.ngZone.run(() => {
        this.handleNotificationCreated(notification);
      });
    });

    this.socket.on("EVENT_STATUS_CHANGED", (payload: any) => {
      this.ngZone.run(() => {
        this.handleEventStatusChanged(payload);
      });
    });

    this.socket.on("connect", () => {
      if (sessionStorage.getItem('isShowConsole')) {
        console.log("Socket connect");
      }
    });

    this.socket.on("disconnect", (data: any) => {
      if (sessionStorage.getItem('isShowConsole')) {
        console.log("Socket disconnect", data);
      }
    });
  }

  private handleLegacyMessage(data: any) {
    if (sessionStorage.getItem('isShowConsole')) {
      console.log("Socket Data Received", data);
    }

    const payload = this.getPayloadObject(data);
    const payloadKeys: Array<string> = Object.keys(payload);
    const producerApproveKey = payloadKeys.find(item => item.includes('PRODUCER_STATUS_APPROVED'));

    if (producerApproveKey) {
      if (this.currentUserData && Object.keys(this.currentUserData).length > 0) {
        this.currentUserData.is_email_verified = payload[producerApproveKey];
        this.commonFunction.isUserApproved.next(payload[producerApproveKey]);
        this.authservice.currentUserDetails.next(this.currentUserData);
      }
      return;
    }

    const negotiationId = this.extractNegotiationId(payload);
    if (negotiationId) {
      this.isMessageDetected.next(negotiationId);
    }

    // Backward compatibility: if legacy MESSAGE sends full notification doc, update immediately.
    if (this.isNotificationDocument(data)) {
      this.pushNotificationToUi(data);
      return;
    }

    if (data?.message) {
      const lowerMessage = String(data.message).trim().toLowerCase();
      if (lowerMessage.startsWith('hello')) {
        return;
      }
      // Prefer NOTIFICATION_CREATED when available; delay MESSAGE fallback slightly.
      this.scheduleLegacyNotificationFallback(data.message);
      return;
    }
  }

  private handleNotificationCreated(notification: any) {
    this.clearLegacyFallbackTimer();
    this.pushNotificationToUi(notification);
  }

  private handleEventStatusChanged(payload: any) {
    if (!payload || typeof payload !== "object") {
      return;
    }

    const normalizedStatus = String(payload.status || "").trim().toLowerCase();
    if (!normalizedStatus) {
      return;
    }

    this.eventStatusChangedSubject.next({
      eventId: payload.eventId ? String(payload.eventId) : "",
      status: normalizedStatus,
      title: typeof payload.title === "string" ? payload.title : "",
      meetCode: typeof payload.meetCode === "string" ? payload.meetCode : "",
      description: typeof payload.description === "string" ? payload.description : ""
    });
  }

  private pushNotificationToUi(notification: any) {
    const isNewNotification = this.negotiationService.pushRealtimeNotification(notification);
    if (isNewNotification && notification?.message) {
      const toastMsg = notification.message.includes('New negotiation') ? 'New offer received.' : notification.message;
      this.commonFunction.showToastSuccess(toastMsg);
    }

    const negotiationId = this.extractNegotiationId(notification?.payload);
    if (negotiationId) {
      this.isMessageDetected.next(negotiationId);
    }
  }

  private getPayloadObject(data: any): any {
    if (data && typeof data === "object" && data.payload && typeof data.payload === "object") {
      return data.payload;
    }
    return {};
  }

  private extractNegotiationId(payload: any): string {
    if (!payload || typeof payload !== "object") {
      return "";
    }
    const negotiationKey = Object.keys(payload).find(item => item.includes('NEGOTIATION'));
    if (negotiationKey && payload[negotiationKey]) {
      return payload[negotiationKey];
    }
    return "";
  }

  private isNotificationDocument(data: any): boolean {
    return !!(
      data &&
      typeof data === "object" &&
      (data._id || data.id) &&
      typeof data.message === "string" &&
      typeof data.createdAt === "string"
    );
  }

  private syncNotificationsOnFocus() {
    if (!this.currentAuthToken) {
      return;
    }
    this.negotiationService.getAllNegotiationNotification('focus-sync', false).subscribe({
      error: (err) => {
        console.error('Failed to sync notifications on window focus:', err);
      }
    });
  }

  private scheduleLegacyNotificationFallback(message: string) {
    this.clearLegacyFallbackTimer();
    this.legacyFallbackTimer = setTimeout(() => {
      const toastMsg = message.includes('New negotiation') ? 'New offer received.' : message;
      this.commonFunction.showToastSuccess(toastMsg);
      this.negotiationService.getAllNegotiationNotification('update', false).subscribe({
        error: (err) => {
          console.error('Failed to fetch notifications on socket message:', err);
        }
      });
      this.legacyFallbackTimer = null;
    }, 750);
  }

  private clearLegacyFallbackTimer() {
    if (this.legacyFallbackTimer) {
      clearTimeout(this.legacyFallbackTimer);
      this.legacyFallbackTimer = null;
    }
  }

  disconnectSocket(keepPublicConnection: boolean = true) {
    this.currentAuthToken = "";
    this.negotiationService.resetNotificationState();
    this.teardownSocket();
    if (keepPublicConnection && this.isSocketInitialized) {
      this.connectOrReconnect("");
    }
  }

  private teardownSocket() {
    this.clearLegacyFallbackTimer();
    if (this.socket) {
      this.socket.removeAllListeners();
      this.socket.disconnect();
      this.socket = null;
    }
  }

  ngOnDestroy(): void {
    this.isSocketInitialized = false;
    this.eventStatusChangedSubject.complete();
    window.removeEventListener('focus', this.onWindowFocus);
    this.teardownSocket();
  }
}
